import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from './utils/axiosinstance';
import ErrorDisplay from './shared/ErrorDisplay';
import ProgressBar from './ProgressBar';

const ProcessingComponent = ({ id, status, setStatus, onComplete, onError }) => {
    console.log("[ProcessingComponent] Render with status =>", status);
    console.log("[ProcessingComponent] Render with id =>", id);
    const [progress, setProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const POLL_INTERVAL = 10000;
    const [message, setMessage] = useState(null);

    // Store the error state in a ref to persist it across re-renders
    const errorRef = useRef(null);
    const hasCalledOnError = useRef(false);

    const handleFailure = (failedStatus) => {
        const errorMsg = `Error: Task Status ${failedStatus}`;
        console.log("[ProcessingComponent] handleFailure called with status =>", failedStatus);
        errorRef.current = errorMsg;
        setErrorMessage(errorMsg);
        setProgress(0);
        setMessage(null);
        setStatus('failed');
        
        // Only call onError once
        if (!hasCalledOnError.current) {
            hasCalledOnError.current = true;
            onError();
        }
    };

    // Reset error flag when status changes from failed to something else
    useEffect(() => {
        if (status !== 'failed') {
            hasCalledOnError.current = false;
        }
    }, [status]);

    // Restore error message whenever it's cleared but should be showing
    useEffect(() => {
        if (errorRef.current && !errorMessage) {
            console.log("[ProcessingComponent] Restoring error message from ref:", errorRef.current);
            setErrorMessage(errorRef.current);
        }
    }, [errorMessage]);

    // Initialize error message if status is failed
    useEffect(() => {
        if (status === 'failed' && !errorMessage && !errorRef.current) {
            handleFailure(status);
        }
    }, [status]);

    useEffect(() => {
        let interval;
        let isComponentMounted = true;

        const updateProgress = async () => {
            try {
                // Skip if we already have an error
                if (errorRef.current) {
                    console.log("[ProcessingComponent] Skipping API call due to existing error");
                    return;
                }

                if (id) {
                    const response = await axiosInstance.get('/api/check_output_status/', {
                        params: { id: id },
                    });
                    console.log("[ProcessingComponent] API Response =>", response.data);
                    
                    if (!isComponentMounted) return;

                    const taskStatus = response.data.video.status || 'failed';
                    console.log("[ProcessingComponent] Task Status =>", taskStatus);
                    
                    setStatus(taskStatus);

                    if (taskStatus === 'failed') {
                        console.log("[ProcessingComponent] Detected failed status");
                        handleFailure(taskStatus);
                        clearInterval(interval);
                        return;
                    }

                    if (taskStatus === 'completed') {
                        console.log("[ProcessingComponent] task completed =>", response.data);
                        clearInterval(interval);
                        onComplete(response.data.video);
                    } else if (taskStatus === 'queued') {
                        setMessage('Job is in queue, please wait...');
                        console.log("[ProcessingComponent] task status queued =>", taskStatus);
                    } else if (taskStatus === 'pre-processing') {
                        setMessage('Pre-processing video, please wait...');
                        console.log("[ProcessingComponent] task status pre-processing =>", taskStatus);
                    } else if (taskStatus === 'in_progress') {
                        console.log("[ProcessingComponent] task status in_progress =>", taskStatus);
                        setMessage('');
                        setProgress(Math.min(98, response.data.video.proportion_complete * 100));
                    } else if (taskStatus === 'getting_s3_video') {
                        setProgress(98);
                    }
                } else if (status === 'failed') {
                    console.log("[ProcessingComponent] No id and status is failed");
                    handleFailure(status);
                }
            } catch (error) {
                console.error("[ProcessingComponent] Error in updateProgress:", error);
                if (isComponentMounted) {
                    handleFailure('failed');
                }
            }
        };

        updateProgress();
        interval = setInterval(updateProgress, POLL_INTERVAL);

        return () => {
            isComponentMounted = false;
            clearInterval(interval);
        };
    }, []);

    // Debug useEffect to track state changes
    useEffect(() => {
        console.log("[ProcessingComponent] State Update:", {
            status,
            errorMessage,
            errorRef: errorRef.current,
            progress,
            message
        });
    }, [status, errorMessage, progress, message]);

    return (
        <div className="processing-container">
            {message && !errorRef.current && (
                <p className="text-white font-bold">{message}</p>
            )}

            {status === 'in_progress' && !errorRef.current && (
                <ProgressBar progress={progress} />
            )}
            
            {(status === 'failed' || errorMessage || errorRef.current) && (
                <ErrorDisplay 
                    errorMessage={errorMessage || errorRef.current || `Error: Task Status ${status}`}
                    onClose={() => {
                        console.log("[ProcessingComponent] ErrorDisplay onClose called");
                        errorRef.current = null;
                        setErrorMessage(null);
                        hasCalledOnError.current = false;
                    }} 
                />
            )}
        </div>
    );
};

export default ProcessingComponent;
